import React from 'react';
import { Box, LinearProgress, Skeleton, Typography } from '@mui/material';

import { IntentTagItem } from '../../../../store/modules/merchant/types';
import { formatCount, formatRate } from './formatMetrics';

type IntentTagPanelProps = {
  items?: IntentTagItem[];
  isLoading?: boolean;
};

/**
 * Intent Tag Analysis panel.
 * Figma marks this section "IN PROGRESS" — renders API distribution until design is finalized.
 */
export const IntentTagPanel = ({ items = [], isLoading }: IntentTagPanelProps) => {
  const topTag = items[0];
  const headline = topTag ? formatRate(topTag.percentage) : '—';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'secondary.main',
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        minHeight: 136,
      }}
      data-testid="ella-intent-tag-panel"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <Typography
          variant="medium"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', lineHeight: '18px' }}
        >
          Intent Tag Analysis
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width="20%" height={36} />
        ) : (
          <Typography
            variant="huge"
            fontWeight="semibold"
            sx={{ lineHeight: '36px', letterSpacing: '-1.5px' }}
          >
            {headline}
          </Typography>
        )}

        {!isLoading && items.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((item) => (
              <Box key={item.tag}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                  <Typography variant="small" sx={{ wordBreak: 'break-word' }}>
                    {item.tag.replace(/_/g, ' ')}
                  </Typography>
                  <Typography variant="small" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatRate(item.percentage)} · {formatCount(item.count)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, item.percentage * 100))}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    backgroundColor: 'secondary.light',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'brand.main',
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {!isLoading && items.length === 0 && (
          <Typography variant="medium" color="text.secondary">
            No intent tag data for this range
          </Typography>
        )}
      </Box>
      <Typography variant="small" color="text.secondary" sx={{ lineHeight: '16px' }}>
        Distribution of shopper intents
      </Typography>
    </Box>
  );
};
